import React, { useState } from 'react'
import { Edit, Trash, Eye, X, Printer, PackagePlus, History } from 'lucide-react'
import StickersPrint from '../Component/StickersPrint'
import AugmentStockModal from '../Component/AugmentStockModal'
import ProductHistoryModal from '../Component/ProductHistoryModal'
import { BASE_URL, EXTERNAL_BASE_URL } from '../apiConfig'
import { useTranslation } from '../LanguageContext'
import { useAuth } from '../AuthContext'

export default function TrTableProducts({ product, onDelete, onEdit, onUpdate }) {
  const [showVariantsModal, setShowVariantsModal] = useState(false)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [showStockModal, setShowStockModal] = useState(false)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const { t } = useTranslation()
  const { can } = useAuth()

  const getStatusStyles = (status) => {
    switch (status) {
      case "in_stock":
        return "bg-green-600 text-white dark:bg-green-900 dark:text-green-200"
      case "low_stock":
        return "bg-amber-500 text-white dark:bg-yellow-900 dark:text-yellow-200"
      case "out_of_stock":
        return "bg-red-500 text-white dark:bg-red-900 dark:text-red-200"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    }
  }

  const getTotalStock = () => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.reduce((sum, variant) => sum + (variant.stock || 0), 0)
    }
    return product.qty || 0
  }

  const getStatus = () => {
    return product.status || "Out of Stock"
  }

  const getStockPercentage = () => {
    const maxStock = 500;
    const totalStock = product.total_stock || getTotalStock()
    return Math.min((totalStock / maxStock) * 100, 100)
  }

  const getVariantCount = () => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.length
    }
    return 0
  }

  const getVariantSummary = () => {
    if (!product.variants || product.variants.length === 0) {
      return t('table.no_variants')
    }
    
    const colors = [...new Set(product.variants.filter(v => v.color).map(v => v.color))]
    const sizes = [...new Set(product.variants.filter(v => v.size).map(v => v.size))]
    
    let summary = ""
    if (colors.length > 0) {
      summary += `${colors.length} color${colors.length > 1 ? 's' : ''}`
    }
    if (sizes.length > 0) {
      if (summary) summary += ", "
      summary += `${sizes.length} size${sizes.length > 1 ? 's' : ''}`
    }
    
    return summary || t('table.has_variants')
  }

  // Prepare data for the modal table (supports colors-only, sizes-only, and both)
  const prepareVariantsData = () => {
    if (!product.variants || product.variants.length === 0) { 
      return { type: 'none', colors: [], sizes: [], matrix: {} }
    }
    
    // Detect variant type
    const hasColors = product.variants.some(v => v.color)
    const hasSizes = product.variants.some(v => v.size)
    
    if (hasColors && !hasSizes) {
      // Colors-only variant
      const colors = product.variants.map(v => ({ 
        name: v.color, 
        value: v.value_color,
        stock: v.stock
      }))
      
      return {
        type: 'colors',
        colors: colors,
        sizes: [],
        matrix: {}
      }
    }
    
    if (!hasColors && hasSizes) {
      // Sizes-only variant
      const sizes = [...new Set(product.variants.map(v => v.size))].sort((a, b) => {
        const sizeOrder = { 'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, 'XXL': 6, 'XXXL': 7 }
        return (sizeOrder[a] || 99) - (sizeOrder[b] || 99)
      })
      
      const sizeStock = {}
      product.variants.forEach(variant => {
        sizeStock[variant.size] = variant.stock
      })
      
      return {
        type: 'sizes',
        colors: [],
        sizes: sizes,
        sizeStock: sizeStock,
        matrix: {}
      }
    }
    
    // Both colors and sizes
    // const colors = [...new Set(product.variants.map(v => ({ 
    //   name: v.color, 
    //   value: v.value_color 
    // })))].sort((a, b) => a.name.localeCompare(b.name))
    const colors = [...new Set(product.variants.map(v => ({ 
      name: v.color || '', 
      value: v.value_color 
    })))].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    
    const sizes = [...new Set(product.variants.map(v => v.size))].sort((a, b) => {
      const sizeOrder = { 'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, 'XXL': 6, 'XXXL': 7 }
      return (sizeOrder[a] || 99) - (sizeOrder[b] || 99)
    })
    
    const matrix = {}
    product.variants.forEach(variant => {
      if (!matrix[variant.color]) matrix[variant.color] = {}
      matrix[variant.color][variant.size] = variant.stock
    })
    
    return { 
      type: 'both',
      colors, 
      sizes, 
      matrix 
    }
  }

  const variantsData = prepareVariantsData()
  
  // Deduplicate colors for both type
  const uniqueColors = variantsData.type === 'both' 
    ? Array.from(new Map(variantsData.colors.map(color => [color.name, color])).values())
    : []

  // Helper to render variant preview in table row
  const renderVariantPreview = () => {
    if (!product.variants || product.variants.length === 0) {
      return <span className="text-xs text-gray-400">{t('table.no_variants')}</span>
    }
    
    const previewVariants = product.variants.slice(0, 2)
    
    return (
      <div className="flex flex-col gap-1">
        {previewVariants.map((variant, index) => (
          <div key={index} className="flex items-center text-nowrap gap-1 text-xs">
            {variant.value_color && (
              <div 
                className="w-3 h-3 rounded border border-gray-300"
                style={{ backgroundColor: variant.value_color || '#ccc' }}
              />
            )}
            <span className="text-gray-600 text-[11px] dark:text-gray-400">
              {variant.color && variant.size && `${variant.color} / ${variant.size}`}
              {variant.color && !variant.size && variant.color}
              {!variant.color && variant.size && variant.size}
            </span>
          </div>
        ))}
        {product.variants.length > 2 && (
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {product.variants.length - 2} {t('table.more')}
          </p>
        )}
      </div>
    )
  }

  // Render variants modal content based on type
  const renderVariantsModalContent = () => {
    if (variantsData.type === 'colors') {
      // Colors-only display
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse rounded-lg border border-gray-400 dark:border-gray-500">
            <thead>
              <tr>
                <th className="text-left p-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-400 dark:border-gray-500">
                  {t('table.color')}
                </th>
                <th className="text-center p-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-400 dark:border-gray-500">
                  {t('table.stock')}
                </th>
              </tr>
            </thead>
            <tbody>
              {variantsData.colors.map((color, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}>
                  <td className="p-2 border border-gray-400 dark:border-gray-500">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded border border-gray-500"
                        style={{ backgroundColor: color.value || '#ccc' }}
                        title={color.name}
                      />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {color.name}
                      </span>
                    </div>
                  </td>
                  <td className="text-center py-2 px-1.5 border border-gray-400 dark:border-gray-500">
                    <span className="text-xs font-medium text-gray-900 dark:text-white">
                      {color.stock} {t('table.pcs')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
    
    if (variantsData.type === 'sizes') {
      // Sizes-only display
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse rounded-lg border border-gray-400 dark:border-gray-500">
            <thead>
              <tr>
                <th className="text-left p-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-400 dark:border-gray-500">
                  {t('table.size')}
                </th>
                <th className="text-center p-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-400 dark:border-gray-500">
                  {t('table.stock')}
                </th>
              </tr>
            </thead>
            <tbody>
              {variantsData.sizes.map((size, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}>
                  <td className="p-2 border border-gray-400 dark:border-gray-500">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {size}
                    </span>
                  </td>
                  <td className="text-center py-2 px-1.5 border border-gray-400 dark:border-gray-500">
                    <span className="text-xs font-medium text-gray-900 dark:text-white">
                      {variantsData.sizeStock?.[size] || 0} {t('table.pcs')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
    
    if (variantsData.type === 'both') {
      // Both colors and sizes display (matrix)
      if (uniqueColors.length === 0 || variantsData.sizes.length === 0) {
        return (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {t('table.no_data')}
          </div>
        )
      }
      
      return (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse rounded-lg border border-gray-400 dark:border-gray-500">
            <thead>
              <tr>
                <th className="text-left p-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-400 dark:border-gray-500">
                  {t('table.color_size')}
                </th>
                {variantsData.sizes.map((size, index) => (
                  <th 
                    key={index}
                    className="text-center p-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-400 dark:border-gray-500"
                  >
                    {size}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {uniqueColors.map((color, colorIndex) => (
                <tr 
                  key={colorIndex}
                  className={colorIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}
                >
                  <td className="p-2 border border-gray-400 dark:border-gray-500">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-4 h-4 rounded border border-gray-500"
                        style={{ backgroundColor: color.value || '#ccc' }}
                        title={color.name}
                      />
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {color.name}
                      </span>
                    </div>
                  </td>
                  {variantsData.sizes.map((size, sizeIndex) => {
                    const stock = variantsData.matrix[color.name]?.[size] || 0
                    return (
                      <td 
                        key={sizeIndex}
                        className="text-center py-2 px-1.5 border border-gray-400 dark:border-gray-500"
                      >
                          {stock > 0 ? (
                          <div className="flex flex-col items-center">
                            <span className="text-xs text-nowrap font-medium text-gray-900 dark:text-white">
                              {stock} <span>{t('table.pcs')}</span>
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    }
    
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        {t('table.no_data')}
      </div>
    )
  }

  return (
    <>
      <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-150">
        {/* Product Info */}
        <td className="px-6 py-2">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 min-w-14 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {product.image ? (
                <img 
                  src={product.source_id ? `${EXTERNAL_BASE_URL}/storage/${product.image}` : `${BASE_URL}/storage/${product.image}`} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null
                    e.target.src = 'https://placehold.co/56x56?text=No+Image'
                  }}
                />
              ) : (
                <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <span className="text-xs text-gray-500">{t('table.no_image')}</span>
                </div>
              )}
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">{product.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {getVariantSummary()}
              </p>
            </div>
          </div>
        </td>

        {/* Price */}
        <td className="px-6 py-2">
          <div>
            <p className="font-medium text-nowrap text-gray-900 dark:text-gray-100">
              {parseFloat(product.price).toFixed(2)} MAD
            </p>
            <p className="text-[11px] text-nowrap text-gray-500 dark:text-gray-400">
              {t('table.cost')} {parseFloat(product.price_achat).toFixed(2)} MAD
            </p>
          </div>
        </td>

        {/* Stock */}
        <td className="px-6 py-2">
          <div className="space-y-1">
            <div className="flex items-center justify-between text-nowrap gap-1 text-xs">
              <span className="text-gray-700 dark:text-gray-300">
                {product.total_stock || getTotalStock()} {t('table.in_stock')}
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {Math.round(getStockPercentage())}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div 
                className={`h-1.5 rounded-full text-nowrap ${
                  getStockPercentage() > 50 ? 'bg-green-500' :
                  getStockPercentage() > 20 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${getStockPercentage()}%` }}
              />
            </div>
          </div>
        </td>

        {/* Variants */}
        <td className="px-6 py-2">
          <div className="flex items-center gap-2">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {getVariantCount()} {getVariantCount() <= 1 ? t('table.variant') : t('table.variants')}
            </p>
            <div>
              {renderVariantPreview()}
            </div>
          </div>
        </td>

        {/* Status */}
        <td className="px-6 py-2 text-center">
          <span className={`px-2 py-[2px] rounded-full text-nowrap text-[10px] font-medium ${getStatusStyles(getStatus())}`}>
            {t('products.'+getStatus())}
          </span>
        </td>

        {/* Actions */}
        <td className="px-6 py-2">
          <div className="flex items-center justify-center gap-2">
            {can('products.stock') && (
              <button
                onClick={() => setShowStockModal(true)}
                className="p-1.5 hover:bg-gray-100 border border-gray-400 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
                title={t('table.augment_stock')}
              >
                <PackagePlus className="w-3.5 h-3.5" />
              </button>
            )}
            {can('variants.view') && product.has_variants && product.variants && product.variants.length > 0 && (
              <button 
                onClick={() => setShowVariantsModal(true)}
                className="p-1.5 hover:bg-gray-100 border border-gray-400 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
                title={t('table.view_variants')}
              >
                <Eye className="w-3.5 h-3.5" />
              </button>
            )}
            {can('products.view') && (
              <button 
                  onClick={() => setShowPrintModal(true)}
                  className="p-1.5 hover:bg-gray-100 border border-gray-400 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
                  title={t('table.print_stickers')}
                >
                  <Printer className="w-3.5 h-3.5" />
              </button>
            )}

            {can('products.view') && (
              <button
                onClick={() => setShowHistoryModal(true)}
                className="p-1.5 hover:bg-gray-100 border border-gray-400 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
                title={t('products.history.title')}
              >
                <History className="w-3.5 h-3.5" />
              </button>
            )}

            {can('products.edit') && (
              <button 
                onClick={() => onEdit(product)}
                className="p-1.5 hover:bg-gray-100 border border-gray-400 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
                title={t('table.edit_product')}
              >
                <Edit className="w-3.5 h-3.5" />
              </button>
            )}
            {can('products.delete') && (
              <button 
                onClick={() => onDelete(product.id)}
                className="p-1.5 hover:bg-red-50 border border-red-400 dark:hover:bg-red-900/20 rounded-lg text-red-400 dark:text-red-400 transition-colors"
                title={t('table.delete_product')}
              >
                <Trash className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Variants Modal */}
      {showVariantsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {product.name} - {t('table.stock')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('table.total_stock')} {product.total_stock || getTotalStock()} {t('table.units')}
                </p>
              </div>
              <button
                onClick={() => setShowVariantsModal(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-4">
              {renderVariantsModalContent()}

              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mt-5">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('table.pricing')}
                </h4>
                <div className="grid grid-cols-3 max-lg:grid-cols-2 gap-3">
                  <div className="flex gap-1 bg-green-100 rounded-lg p-2 justify-between">
                    <span className="text-xs text-gray-600 dark:text-gray-400">{t('table.selling_price')}</span>
                    <span className="text-xs font-medium text-green-600 dark:text-green-400">
                      {parseFloat(product.price).toFixed(2)} MAD
                    </span>
                  </div>
                  <div className="flex gap-1 bg-yellow-100 rounded-lg p-2 justify-between">
                    <span className="text-xs text-gray-600 dark:text-gray-400">{t('table.cost_price')}</span>
                    <span className="text-xs font-medium text-yellow-900 dark:text-white">
                      {parseFloat(product.price_achat).toFixed(2)} MAD
                    </span>
                  </div>
                  <div className="flex gap-1 bg-blue-100 rounded-lg p-2 justify-between">
                    <span className="text-xs text-gray-600 dark:text-gray-400">{t('table.margin')}</span>
                    <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                      {(parseFloat(product.price) - parseFloat(product.price_achat)).toFixed(2)} MAD
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700 gap-2">
              <button
                onClick={() => setShowVariantsModal(false)}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {t('table.close')}
              </button>
              <button
                onClick={() => {
                  setShowVariantsModal(false)
                  onEdit(product)
                }}
                className="p-2 hover:bg-gray-100 border border-gray-400 dark:hover:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 transition-colors"
              >
                <Edit className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Modal */}
      {showPrintModal && (
        <StickersPrint 
          product={product} 
          onClose={() => setShowPrintModal(false)} 
        />
      )}

      {/* Augment Stock Modal */}
      {showStockModal && (
        <AugmentStockModal
          product={product}
          onClose={() => setShowStockModal(false)}
          onUpdated={onUpdate}
        />
      )}

      {/* Product History Modal */}
      {showHistoryModal && (
        <ProductHistoryModal
          product={product}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </>
  )
}